import React, { useCallback, useEffect, useState } from 'react';
import { api } from '../services/api';
import { NetworkLocation, UnresolvedDocument } from '../types';

interface Props {
  /** Osvežava radne liste kad nalog dobije odredište i uđe u planiranje. */
  onRecorded: () => void;
}

/**
 * Nalozi kojima ERP nije rekao gde idu.
 *
 * Ovo je mreža za retke slučajeve, ne svakodnevni posao: pošto se glava naloga
 * čita iz prave tabele, sve što danas čeka prevoz ima odredište. Ostaje nalog
 * kome čovek nije upisao opis — tada operater pozove onoga ko ga je napravio i
 * upiše šta mu je rečeno.
 *
 * Upis ide SAMO u našu bazu. Nalog u ERP-u ostaje netaknut.
 */
export default function DestinationFixer({ onRecorded }: Props) {
  const [documents, setDocuments] = useState<UnresolvedDocument[]>([]);
  const [locations, setLocations] = useState<NetworkLocation[]>([]);
  const [open, setOpen] = useState<string | null>(null);
  const [choice, setChoice] = useState('');
  const [note, setNote] = useState('');
  const [learn, setLearn] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setDocuments(await api.unresolvedDocuments());
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }, []);

  useEffect(() => {
    void load();
    void api.locations().then(setLocations).catch(() => setLocations([]));
  }, [load]);

  const start = (document: UnresolvedDocument) => {
    setOpen(open === document.sourceDocument ? null : document.sourceDocument);
    setChoice('');
    setNote('');
    // Pamćenje se nudi samo kad ima šta da se zapamti; kod naloga bez opisa
    // „prazno znači Kula" poslalo bi tamo svaki neopisan nalog.
    setLearn(false);
  };

  const save = async (document: UnresolvedDocument) => {
    const [kind, code] = choice.split(':');
    if (!code) return;

    setBusy(true);
    try {
      await api.recordDestination({
        sourceDocument: document.sourceDocument,
        destinationKind: kind,
        destinationCode: code,
        rawText: document.rawText,
        note: note.trim() || null,
        learnPhrase: learn && !!document.rawText
      });

      setOpen(null);
      await load();
      onRecorded();
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  if (documents.length === 0) {
    return null;
  }

  return (
    <section className="panel panel--todo">
      <h2>
        Bez odredišta{' '}
        {/* I naloga i stavki: spisak levo broji STAVKE, pa je broj naloga sam
            izgledao kao da se dva ekrana ne slažu. */}
        <span className="muted">
          · {documents.length} {documents.length === 1 ? 'nalog' : 'naloga'}
          {' · '}{documents.reduce((sum, d) => sum + d.items.length, 0)} stavki
        </span>
      </h2>

      <p className="note">
        Nalog ne kaže gde roba ide, pa se ne može planirati. Pozovi onoga ko je
        dokument napravio i upiši šta ti kaže — upisuje se kod nas, nalog u ERP-u
        ostaje netaknut.
      </p>

      {error && <div className="health health--bad">{error}</div>}

      <ul className="unresolved">
        {documents.map((document) => (
          <li key={document.sourceDocument}>
            <button
              type="button"
              className="disc__head"
              aria-expanded={open === document.sourceDocument}
              onClick={() => start(document)}
            >
              <span className="disc__caret" aria-hidden="true">›</span>

              <span className="disc__title">
                Nalog {document.sourceDocument}
                <span className="unresolved__text">
                  {document.rawText ? `„${document.rawText}"` : 'bez opisa'}
                </span>
              </span>

              <span className="row__side muted">
                {document.quantity} kom · čeka {document.oldestWaitingDays} d
              </span>
            </button>

            {open === document.sourceDocument && (
              <div className="unresolved__form">
                <p className="muted">{document.items.join(', ')}</p>

                <label>
                  Roba ide u
                  <select value={choice} onChange={(e) => setChoice(e.target.value)}>
                    <option value="">— izaberi —</option>
                    {locations.map((location) => (
                      <option key={`${location.kind}:${location.code}`} value={`${location.kind}:${location.code}`}>
                        {location.name} ({location.code})
                      </option>
                    ))}
                  </select>
                </label>

                <label>
                  Ko je rekao
                  <input
                    value={note}
                    placeholder="npr. Pera iz Kule, telefonom"
                    onChange={(e) => setNote(e.target.value)}
                  />
                </label>

                {/* Pamti se samo na izričit zahtev: tiho pamćenje bi značilo da
                    jedan tipfeler zauvek šalje robu u pogrešan grad. */}
                {document.rawText && (
                  <label className="unresolved__learn">
                    <input type="checkbox" checked={learn} onChange={(e) => setLearn(e.target.checked)} />
                    Zapamti da „{document.rawText}" znači ovo odredište
                  </label>
                )}

                <div className="actions">
                  <button type="button" className="primary" disabled={busy || !choice} onClick={() => void save(document)}>
                    Upiši odredište
                  </button>
                  <button type="button" className="link" onClick={() => setOpen(null)}>
                    Odustani
                  </button>
                </div>
              </div>
            )}
          </li>
        ))}
      </ul>
    </section>
  );
}
